import { useEffect, useRef } from 'react'
import { resolveWorkspaceKey, type WorkspaceCommand, type WorkspaceKeyState } from '@/lib/canvas/workspace-commands'

function targetOf(event: KeyboardEvent) {
  const element = event.target as HTMLElement | null
  if (!element || typeof element.closest !== 'function') return null
  return {
    tagName: element.tagName,
    isContentEditable: element.isContentEditable,
    value: 'value' in element ? String((element as HTMLInputElement).value ?? '') : undefined,
    closest: (selector: string) => element.closest(selector),
  }
}

/** Capture-phase listener so workspace chords are not also handled by the canvas map. */
export function useWorkspaceKeys(getState: () => Omit<WorkspaceKeyState, 'target'>, onCommand: (command: WorkspaceCommand) => void) {
  const getStateRef = useRef(getState)
  const onCommandRef = useRef(onCommand)
  getStateRef.current = getState
  onCommandRef.current = onCommand
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const command = resolveWorkspaceKey({
        key: event.key,
        code: event.code,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        repeat: event.repeat,
        isComposing: event.isComposing,
        keyCode: event.keyCode,
      }, { ...getStateRef.current(), target: targetOf(event) })
      if (!command) return
      event.preventDefault()
      event.stopPropagation()
      onCommandRef.current(command)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])
}
